import { FirebaseApp } from 'firebase/app';
import {
  AppCheck,
  getToken,
  initializeAppCheck,
  onTokenChanged,
  ReCaptchaV3Provider,
} from 'firebase/app-check';
import { getErrorReporter } from 'shared/services/error-tracking';

let appCheck: AppCheck | undefined;
let callInitialized = false;
let onlineListener: (() => void) | undefined;
let refreshFailureReported = false;
let sawValidToken = false;
let initializedAt = 0;

const removeOnlineListener = () => {
  if (!onlineListener) {
    return;
  }

  window.removeEventListener('online', onlineListener);
  onlineListener = undefined;
};

// A startup failure retries on a growing backoff, so only a previously-held valid token marks a degraded session.
const watchForRefreshFailures = (instance: AppCheck) => {
  onTokenChanged(
    instance,
    () => {
      sawValidToken = true;
    },
    (error) => {
      if (refreshFailureReported || !sawValidToken) {
        return;
      }

      refreshFailureReported = true;

      getErrorReporter().captureException(error, {
        tags: { source: 'app-check-refresh-failure' },
        extra: {
          secondsSinceInit: Math.round((Date.now() - initializedAt) / 1000),
        },
      });
    },
  );
};

// A 403/404 from the token exchange throttles the provider for 24h in memory — surface it at startup.
const probeToken = async (instance: AppCheck) => {
  try {
    await getToken(instance);
  } catch (error) {
    getErrorReporter().captureException(error, {
      tags: { source: 'app-check-probe' },
      extra: { code: (error as { code?: string }).code },
    });
  }
};

const initializeAppCheckWhenOnline = (app: FirebaseApp) => {
  if (callInitialized) {
    return;
  }

  if (!navigator.onLine) {
    removeOnlineListener();
    onlineListener = () => {
      initializeAppCheckWhenOnline(app);
    };
    window.addEventListener('online', onlineListener, { once: true });
    return;
  }

  if (import.meta.env.MODE === 'development') {
    // eslint-disable-next-line no-restricted-globals -- Debug is only turned on locally, which is needed to make it work on localhost
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(
        import.meta.env.VITE_FIREBASE_RECAPTCHA_KEY,
      ),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    // Leaving callInitialized false lets a later call retry instead of stranding the session tokenless.
    getErrorReporter().captureException(error, {
      tags: { source: 'app-check-init' },
    });
    return;
  }

  callInitialized = true;
  initializedAt = Date.now();
  removeOnlineListener();
  watchForRefreshFailures(appCheck);
  void probeToken(appCheck);
};

export const initAppCheck = (app: FirebaseApp) => {
  if (appCheck) {
    return appCheck;
  }

  initializeAppCheckWhenOnline(app);

  return appCheck;
};

export const clearAppCheck = () => {
  appCheck = undefined;
  callInitialized = false;
  refreshFailureReported = false;
  sawValidToken = false;
  initializedAt = 0;
  removeOnlineListener();
};
